<template>
  <aside class="flow-library">
    <div class="flow-library-header">
      <div>
        <span class="flow-library-eyebrow">Build your flow</span>
        <h2>Step library</h2>
      </div>
      <span class="badge badge-ghost">{{ definitions.length }}</span>
    </div>
    <label class="flow-library-search">
      <Icon icon="tabler:search" width="17" height="17" />
      <input v-model="searchValue" type="search" placeholder="Find a step" aria-label="Find a step" />
    </label>
    <div class="flow-library-content">
      <section v-for="group in groupedDefinitions" :key="group.name" class="flow-library-group">
        <h3>{{ group.name }}</h3>
        <button
          v-for="definition in group.items"
          :key="definition.id"
          type="button"
          class="flow-library-item"
          draggable="true"
          @dragstart="startDrag($event, definition)"
          @click="emit('add-node', definition)"
        >
          <span class="flow-library-item-icon" :class="{ 'flow-library-item-tool-icon': definition.isConnectedTool === true }">
            <img v-if="hasDefinitionImage(definition) === true" :src="definition.image" :alt="definition.label" @error="markDefinitionImageFailed(definition.id)" />
            <Icon v-else :icon="definition.icon" width="18" height="18" />
          </span>
          <span class="flow-library-item-copy">
            <strong>{{ definition.label }}</strong>
            <small>{{ definition.description }}</small>
          </span>
          <Icon icon="tabler:plus" width="17" height="17" class="flow-library-add" />
        </button>
      </section>
      <div v-if="groupedDefinitions.length === 0" class="flow-library-empty">
        <Icon icon="tabler:search-off" width="26" height="26" />
        <span>No matching steps</span>
      </div>
    </div>
    <div class="flow-library-tip">
      <Icon icon="tabler:bulb" width="17" height="17" />
      <p>Click a step to add it. Connect steps by dragging from the right dot to the next step.</p>
    </div>
  </aside>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';

const props = defineProps({
  definitions: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['add-node', 'drag-node']);
const searchValue = ref('');
const failedImageDefinitionIds = ref([]);

const groupedDefinitions = computed(() => {
  const query = searchValue.value.trim().toLowerCase();
  const groups = [];
  const groupMap = new Map();
  for (let index = 0; index < props.definitions.length; index = index + 1) {
    const definition = props.definitions[index];
    const searchable = `${definition.label} ${definition.description} ${definition.group}`.toLowerCase();
    if (query.length > 0 && searchable.includes(query) === false) {
      continue;
    }
    if (groupMap.has(definition.group) === false) {
      const group = { name: definition.group, items: [] };
      groupMap.set(definition.group, group);
      groups.push(group);
    }
    groupMap.get(definition.group).items.push(definition);
  }
  return groups;
});

function startDrag(event, definition) {
  if (event.dataTransfer !== null && event.dataTransfer !== undefined) {
    event.dataTransfer.setData('application/flow-manager', definition.id);
    event.dataTransfer.effectAllowed = 'copy';
  }
  emit('drag-node', definition);
}

function hasDefinitionImage(definition) {
  if (definition === null || definition === undefined) {
    return false;
  }
  if (typeof definition.image !== 'string' || definition.image.trim().length === 0) {
    return false;
  }
  return failedImageDefinitionIds.value.includes(definition.id) === false;
}

function markDefinitionImageFailed(definitionId) {
  if (failedImageDefinitionIds.value.includes(definitionId) === true) {
    return;
  }
  failedImageDefinitionIds.value = [...failedImageDefinitionIds.value, definitionId];
}
</script>

<style scoped>
.flow-library {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: #f8fafc;
  border-right: 1px solid #d8e1ed;
}

.flow-library-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1rem 0.75rem;
}

.flow-library-eyebrow {
  color: #2f72c7;
  font-size: 0.61rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.flow-library-header h2 {
  margin: 0.24rem 0 0;
  color: #10233e;
  font-size: 0.95rem;
  font-weight: 720;
}

.flow-library-search {
  display: flex;
  align-items: center;
  margin: 0 1rem 0.85rem;
  padding: 0 0.7rem;
  height: 2.5rem;
  border: 1px solid #d1dce9;
  border-radius: 0.45rem;
  background: #ffffff;
  color: #61718a;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.flow-library-search:focus-within {
  border-color: #4c82bf;
  box-shadow: 0 0 0 3px rgba(47, 114, 199, 0.11);
}

.flow-library-search input {
  min-width: 0;
  width: 100%;
  margin-left: 0.45rem;
  border: 0;
  outline: 0;
  background: transparent;
  color: #172033;
  font-size: 0.78rem;
}

.flow-library-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 0.75rem 0.85rem;
}

.flow-library-group + .flow-library-group {
  margin-top: 1.35rem;
}

.flow-library-group h3 {
  margin: 0 0.45rem 0.4rem;
  color: #718198;
  font-size: 0.59rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.flow-library-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 3.9rem;
  padding: 0.6rem;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  background: transparent;
  color: #10233e;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.flow-library-item + .flow-library-item {
  margin-top: 0.4rem;
}

.flow-library-item:hover {
  border-color: #d4e0ed;
  background: #ffffff;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.045);
}

.flow-library-item-icon {
  width: 2.2rem;
  height: 2.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dce6f1;
  border-radius: 0.45rem;
  background: #edf3f9;
  color: #34516f;
  flex-shrink: 0;
}

.flow-library-item-icon img {
  width: 1.4rem;
  height: 1.4rem;
  object-fit: contain;
}

.flow-library-item-tool-icon {
  border-color: #ccdceb;
  background: #edf5fc;
  color: #2f72c7;
}

.flow-library-item-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-left: 0.55rem;
}

.flow-library-item-copy strong {
  font-size: 0.74rem;
  font-weight: 720;
}

.flow-library-item-copy small {
  margin-top: 0.1rem;
  color: #687a90;
  font-size: 0.63rem;
  line-height: 1.35;
}

.flow-library-add {
  margin-left: auto;
  color: #6d89a8;
  flex-shrink: 0;
}

.flow-library-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.5rem 1rem;
  color: #94a3b8;
  font-size: 0.75rem;
}

.flow-library-empty span {
  margin-top: 0.5rem;
}

.flow-library-tip {
  display: flex;
  align-items: flex-start;
  margin: 0 0.75rem 0.75rem;
  padding: 0.75rem;
  border: 1px solid #dbe6f1;
  border-radius: 0.5rem;
  background: #edf4fb;
  color: #4e6681;
}

.flow-library-tip svg {
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.flow-library-tip p {
  margin: 0 0 0 0.45rem;
  font-size: 0.66rem;
  line-height: 1.45;
}
</style>
